// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { DEFAULT_PANORAMA_BUBBLE_RADIUS } from 'constants/index';
import { getSpriteTexture } from '../../../customization/utils/setPanoramaMarkerColor.js';

class Image360 {
    constructor(file, time, longitude, latitude, altitude, course, pitch, roll) {
        this.file = file;
        this.time = time;
        this.longitude = longitude;
        this.latitude = latitude;
        this.altitude = altitude;
        this.course = course;
        this.pitch = pitch;
        this.roll = roll;
        this.mesh = null;
        this.texture = null;
        this.texturePromise = null;
    }
}

// Function to create a marker sprite
function createMarkerSprite(hoverOptions, markerColor) {
    const spriteMaterial = new THREE.SpriteMaterial({
        map: getSpriteTexture(markerColor),
        depthWrite: false,
        transparent: true,
        opacity: 1.0,
        sizeAttenuation: true,
    });

    const sprite = new THREE.Sprite(spriteMaterial);

    // Scale the sprite
    const s = DEFAULT_PANORAMA_BUBBLE_RADIUS * 6;
    sprite.scale.set(s, s, 1);
    sprite.userData.originalScale = sprite.scale.clone();
    sprite.userData.targetScale = sprite.scale.clone();
    sprite.userData.hoverOptions = hoverOptions;
    sprite.userData.isCurrentlyHovered = false;
    sprite.userData.isAnimating = false;
    sprite.userData.isLoading = false;
    return sprite;
}

function showLoadingOverlay() {
    // Avoid stacking multiple overlays
    const existing = document.getElementById('panorama-loading-overlay');
    if (existing) return () => {};

    const overlay = document.createElement('div');
    overlay.id = 'panorama-loading-overlay';
    overlay.style.cssText = `
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.65);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        font-family: 'Segoe UI', system-ui, sans-serif;
        color: #fff;
        gap: 18px;
        pointer-events: none;
        animation: pano-fade-in 0.2s ease;
    `;

    // Inject keyframes once
    if (!document.getElementById('panorama-overlay-styles')) {
        const style = document.createElement('style');
        style.id = 'panorama-overlay-styles';
        style.textContent = `
            @keyframes pano-fade-in  { from { opacity: 0 } to { opacity: 1 } }
            @keyframes pano-fade-out { from { opacity: 1 } to { opacity: 0 } }
            @keyframes pano-spin {
                0%   { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    }

    // Spinner ring
    const spinner = document.createElement('div');
    spinner.style.cssText = `
        width: 52px;
        height: 52px;
        border: 4px solid rgba(255,255,255,0.15);
        border-top-color: #fff;
        border-radius: 50%;
        animation: pano-spin 0.8s linear infinite;
    `;

    const label = document.createElement('span');
    label.textContent = 'Loading 360° view…';
    label.style.cssText = `
        font-size: 15px;
        font-weight: 500;
        letter-spacing: 0.02em;
        opacity: 0.9;
    `;

    overlay.appendChild(spinner);
    overlay.appendChild(label);

    const container = viewer?.renderer?.domElement?.parentElement ?? document.body;

    if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
    }
    container.appendChild(overlay);

    return function hideOverlay() {
        overlay.style.animation = 'pano-fade-out 0.25s ease forwards';
        overlay.addEventListener('animationend', () => overlay.remove(), { once: true });
    };
}

function buildSphereGeometry(imageWidth: number, imageHeight: number) {
    const imageAspect = imageWidth / imageHeight;
    const standardAspect = 2.0;

    const deviation = imageAspect / standardAspect;
    if (deviation <= 1.1) {
        return new THREE.SphereGeometry(1, 128, 128);
    }

    const verticalCoverage = standardAspect / imageAspect;
    const phiLength = Math.PI * verticalCoverage;
    const gap = Math.PI - phiLength;

    // Spherical-ish (2.4:1) -> all gap to bottom
    // Cylindrical (4:1) -> equal gaps top and bottom
    const phiStart = deviation >= 2.0 ? gap / 2 : gap;

    return new THREE.SphereGeometry(1, 128, 128, 0, Math.PI * 2, phiStart, phiLength);
}
/**
 * Load the texture for a single Image360 on demand.
 */
function loadTextureOnDemand(image360) {
    if (image360.texturePromise) {
        return image360.texturePromise;
    }

    const $AuthToken = PSPCustomization.getAuthToken();

    image360.texturePromise = fetch(image360.file, {
        headers: {
            Authorization: `Bearer ${$AuthToken}`,
        },
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.blob();
        })
        .then((blob) => {
            const imageUrl = URL.createObjectURL(blob);

            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    image360.imageWidth = img.width;
                    image360.imageHeight = img.height;

                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);

                    const texture = new THREE.CanvasTexture(canvas);
                    texture.mapping = THREE.EquirectangularReflectionMapping;
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.ClampToEdgeWrapping;
                    texture.flipY = false;

                    image360.texture = texture;
                    URL.revokeObjectURL(imageUrl);
                    resolve(texture);
                };
                img.onerror = () => {
                    URL.revokeObjectURL(imageUrl);
                    reject(new Error('Failed to load image'));
                };
                img.src = imageUrl;
            });
        });

    return image360.texturePromise;
}

export const image360Mixin = {
    Images360Loader: {
        load: async function (dataSource, viewer, params = {}) {
            if (!params.transform) {
                params.transform = {
                    forward: (a) => a,
                };
            }

            if (!params.datasetId) {
                log.error('Images360Loader.load: params.datasetId is required, got: ', params.datasetId);
                return;
            }

            const { datasetId } = params;

            if (!this.images) {
                this.images = {};
            }

            const hoverOptions = {
                scaleMultiplier: 1.15,
                animationSpeed: 0.1,

                ...params.hoverOptions,
            };

            let coordinateLines = [];
            if (typeof dataSource === 'string') {
                const response = await fetch(`${dataSource}/coordinates.txt`);
                const text = await response.text();
                const lines = text.split(/\r?\n/);
                coordinateLines = lines.slice(1);
            } else if (Array.isArray(dataSource)) {
                coordinateLines = dataSource;
            }

            const images360 = new Potree.Images360(viewer);
            images360.datasetId = datasetId;
            this.images[datasetId] = images360;
            images360.enableViewing = params.enableViewing === true;

            for (const line of coordinateLines) {
                if (line.trim().length === 0) continue;

                const tokens = line.split(/,/);
                let [filename, time, long, lat, alt, course, pitch, roll] = tokens;

                time = parseFloat(time);
                long = parseFloat(long);
                lat = parseFloat(lat);
                alt = parseFloat(alt);
                course = parseFloat(course);
                pitch = parseFloat(pitch);
                roll = parseFloat(roll);

                const image360 = new Image360(filename, time, long, lat, alt, course, pitch, roll);
                const xy = params.transform.forward([long, lat]);
                const position = [...xy, alt];

                image360.position = position;
                image360.visible = params.enableViewing === true;
                image360.textureLoaded = false; // Track texture loading state

                images360.images.push(image360);
                //images360.totalImages++; // Increment total count
            }

            this.createSceneNodes(images360, params.transform, hoverOptions);
            this.overrideHoverMethods(images360, hoverOptions);
            this.overrideFocusUnfocus(images360, viewer);

            return images360;
        },

        overrideFocusUnfocus(images360, viewer) {
            // Ensure the exit/unfocus button exists
            try {
                if (!images360.elUnfocus) {
                    const elUnfocus = document.createElement('input');
                    elUnfocus.type = 'button';
                    elUnfocus.value = 'Exit 360 View';
                    elUnfocus.style.position = 'absolute';
                    elUnfocus.style.right = '10px';
                    elUnfocus.style.bottom = '10px';
                    elUnfocus.style.zIndex = '10000';
                    elUnfocus.style.fontSize = '1.25em';
                    elUnfocus.style.display = 'none';
                    elUnfocus.addEventListener('click', () => {
                        if (typeof images360.unfocus === 'function') {
                            images360.unfocus();
                        }
                    });

                    images360.elUnfocus = elUnfocus;

                    // Append to viewer DOM root when available, otherwise to body
                    try {
                        images360.domRoot = viewer?.renderer?.domElement?.parentElement ?? document.body;
                        images360.domRoot.appendChild(elUnfocus);
                    } catch (err) {
                        // fallback
                        document.body.appendChild(elUnfocus);
                    }
                }
            } catch (err) {
                console.error('Failed to ensure elUnfocus exists', err);
            }
            // Store original methods
            const originalUnfocus = images360.unfocus.bind(images360);

            images360.focus = function (image360) {
                const pointcloudLayerSavedState = {};
                const pointcloudLayerNode = $('#jstree_scene').jstree(true).get_node('pointclouds');
                if (pointcloudLayerNode) {
                    pointcloudLayerNode.children_d.forEach((childId) => {
                        const childNode = $('#jstree_scene').jstree(true).get_node(childId);
                        if (childNode) {
                            pointcloudLayerSavedState[childId] = Object.assign({}, childNode.state);
                            if (childNode.state.checked) {
                                $('#jstree_scene').jstree(true).uncheck_node(childId);
                            }
                        }
                    });
                    localStorage.setItem('panoramaPointcloudLayerStates', JSON.stringify(pointcloudLayerSavedState));
                }

                const btnToggle = document.getElementsByClassName('btntoggle');
                if (btnToggle.length > 0) {
                    btnToggle[0].style.pointerEvents = 'none';
                    if (btnToggle[0].parentElement?.classList.contains('slideopen')) {
                        btnToggle[0].click();
                    }
                }
                const rightTopMenu = document.getElementsByClassName('righttopmenu');
                if (rightTopMenu.length > 0) {
                    rightTopMenu[0].style.pointerEvents = 'none';
                }
                const sidebarMenu = document.getElementsByClassName('sidebar');
                if (sidebarMenu.length > 0) {
                    sidebarMenu[0].style.pointerEvents = 'none';
                }

                const topLevelChecked = $('#jstree_scene')
                    .jstree(true)
                    .get_checked(true)
                    .filter((n) => n.parent === '#');
                if (topLevelChecked.length > 0) {
                    const nodeStates = {};
                    for (let i = 0; i < topLevelChecked.length; i++) {
                        const id = topLevelChecked[i].id;
                        if (id !== 'pointclouds') {
                            const savedState = $('#jstree_scene').jstree(true).get_node(id).state;
                            if (savedState) {
                                nodeStates[id] = Object.assign({}, savedState);
                            }
                            $('#jstree_scene').jstree(true).uncheck_node(id);
                        }
                    }
                    localStorage.setItem('panoramaSceneStates', JSON.stringify(nodeStates));
                }

                if (this.focusedImage !== null) {
                    this.unfocus();
                }

                // Store the view in a protected instance variable, not the global previousView
                this._protectedPreviousView = {
                    controls: this.viewer.controls,
                    position: this.viewer.scene.view.position.clone(),
                    target: this.viewer.scene.view.getPivot().clone(),
                };

                this.viewer.setControls(this.viewer.orbitControls);
                this.viewer.orbitControls.doubleClockZoomEnabled = false;

                for (let image of this.images) {
                    image.visible = false;
                    image.mesh.visible = false;
                }

                this.selectingEnabled = false;
                this.sphere.visible = false;

                {
                    // orientation
                    let { course, pitch, roll } = image360;
                    this.sphere.rotation.set(
                        THREE.Math.degToRad(roll),
                        THREE.Math.degToRad(pitch),
                        THREE.Math.degToRad(course),
                        'ZYX'
                    );
                }

                this.sphere.position.set(...image360.position);

                const target = new THREE.Vector3(...image360.position);
                const dir = target.clone().sub(viewer.scene.view.position);

                // Keep horizontal direction
                dir.z = 0;
                dir.normalize();

                const newCamPos = target.clone().sub(dir.multiplyScalar(0.000001));

                viewer.scene.view.setView(newCamPos, target, 500);

                this.focusedImage = image360;

                this.elUnfocus.style.display = '';

                // Load texture on demand
                if (image360.texture) {
                    this.sphere.visible = true;
                    this.sphere.material.map = image360.texture;
                    this.sphere.material.needsUpdate = true;
                } else {
                    //Show loading overlay while the texture fetches
                    const hideOverlay = showLoadingOverlay();

                    loadTextureOnDemand(image360)
                        .then((texture) => {
                            hideOverlay();
                            if (this.focusedImage === image360) {
                                const { imageWidth, imageHeight } = image360;
                                this.sphere.geometry.dispose();
                                this.sphere.geometry = buildSphereGeometry(imageWidth, imageHeight);

                                this.sphere.visible = true;
                                this.sphere.material.map = texture;
                                this.sphere.material.needsUpdate = true;
                            }
                        })
                        .catch((err) => {
                            hideOverlay();
                            console.error('Failed to load panorama texture:', image360.file, err);
                        });
                }

                const handleEscapeKey = (event) => {
                    if (event.key === 'Escape' || event.key === 'Esc') {
                        this.unfocus();
                        document.removeEventListener('keydown', handleEscapeKey);
                    }
                };
                document.addEventListener('keydown', handleEscapeKey);
            };

            images360.unfocus = function () {
                const pointcloudLayerStatesStr = localStorage.getItem('panoramaPointcloudLayerStates');
                if (pointcloudLayerStatesStr) {
                    const pointcloudLayerStates = JSON.parse(pointcloudLayerStatesStr);
                    for (const id in pointcloudLayerStates) {
                        const state = pointcloudLayerStates[id];
                        if (state.checked) {
                            $('#jstree_scene').jstree(true).check_node(id);
                        }
                    }
                    localStorage.removeItem('panoramaPointcloudLayerStates');
                }
                const btnToggle = document.getElementsByClassName('btntoggle');
                if (btnToggle.length > 0) {
                    btnToggle[0].style.pointerEvents = 'auto';
                }
                const rightTopMenu = document.getElementsByClassName('righttopmenu');
                if (rightTopMenu.length > 0) {
                    rightTopMenu[0].style.pointerEvents = 'auto';
                }
                const sidebarMenu = document.getElementsByClassName('sidebar');
                if (sidebarMenu.length > 0) {
                    sidebarMenu[0].style.pointerEvents = 'auto';
                }

                const nodeStatesStr = localStorage.getItem('panoramaSceneStates');
                if (nodeStatesStr) {
                    const nodeStates = JSON.parse(nodeStatesStr);
                    for (const id in nodeStates) {
                        const state = nodeStates[id];
                        if (state.checked) {
                            $('#jstree_scene').jstree(true).check_node(id);
                        }
                    }
                    localStorage.removeItem('panoramaSceneStates');
                }

                // Remove any stale loading overlay
                const overlay = document.getElementById('panorama-loading-overlay');
                if (overlay) {
                    overlay.remove();
                }

                this.selectingEnabled = true;

                for (const image of this.images) {
                    image.visible = true;
                    image.mesh.visible = true;
                }

                const image = this.focusedImage;
                if (image === null) {
                    return;
                }

                this.sphere.material.map = null;
                this.sphere.material.needsUpdate = true;
                this.sphere.visible = false;

                // Dispose GPU texture
                if (this.focusedImage?.texture) {
                    this.focusedImage.texture.dispose();
                    this.focusedImage.texture = null;

                    // Clear cached promise so future opens will re-fetch the image
                    this.focusedImage.texturePromise = null;
                }

                // Restore from our protected view, not the global previousView
                if (this._protectedPreviousView) {
                    this.viewer.orbitControls.doubleClockZoomEnabled = true;
                    this.viewer.setControls(this._protectedPreviousView.controls);

                    this.viewer.scene.view.setView(
                        this._protectedPreviousView.position,
                        this._protectedPreviousView.target,
                        0
                    );

                    // Clear the protected view
                    this._protectedPreviousView = null;
                } else {
                    console.warn('No protected previous view found');
                    // Fallback to original unfocus behavior
                    originalUnfocus();
                    return;
                }

                this.focusedImage = null;
                this.elUnfocus.style.display = 'none';
            };
        },

        /**
         * Creates sprite markers only - no texture loading
         * Textures load on first click via loadTextureOnDemand()
         */
        createSceneNodes(images360, transform, hoverOptions) {
            for (const image360 of images360.images) {
                const { longitude, latitude, altitude } = image360;
                const xy = transform.forward([longitude, latitude]);

                const savedColor = PSPCustomization.getPanoramaMarker() || 'green';
                const sprite = createMarkerSprite(hoverOptions, savedColor);
                sprite.position.set(...xy, altitude);
                sprite.image360 = image360;
                sprite.visible = image360.visible;

                images360.node.add(sprite);
                image360.mesh = sprite;
                image360.textureLoaded = false;
            }
        },

        overrideHoverMethods(images360, hoverOptions = {}) {
            images360.currentlyHovered = null;
            images360.removeEventListener('mousedown', images360._listeners['mousedown'][0]);

            images360.addEventListener('mousedown', () => {
                // Find which Images360 instance has the currently hovered object
                let activeImages360Instance = null;
                let activeHoveredObject = null;

                if (images360.viewer && images360.viewer.scene && images360.viewer.scene.images360) {
                    images360.viewer.scene.images360.forEach((images360Instance) => {
                        if (images360Instance.currentlyHovered && images360Instance.currentlyHovered.image360) {
                            activeImages360Instance = images360Instance;
                            activeHoveredObject = images360Instance.currentlyHovered;
                        }
                    });
                }

                // Only proceed if this is the active instance
                if (activeImages360Instance === images360 && activeHoveredObject) {
                    const toolbar = document.getElementsByClassName('righttopmenu')[0];
                    images360.elUnfocus.style.top = 0;
                    images360.elUnfocus.style.right = toolbar ? `${toolbar.clientWidth}px` : '10px';
                    images360.elUnfocus.style.height = toolbar ? `${toolbar.clientHeight}px` : '58px';
                    images360.elUnfocus.style.fontSize = '1em';
                    images360.elUnfocus.style.border = 'none';
                    images360.elUnfocus.style.color = '#fff';
                    images360.elUnfocus.style.backgroundColor = 'var(--main-color)';
                    images360.elUnfocus.innerText = 'Exit 360 View';
                    images360.elUnfocus.value = 'Exit 360 View';
                    images360.elUnfocus.style.cursor = 'pointer';

                    images360.focus(activeHoveredObject.image360);
                }
            });

            const raycaster = new THREE.Raycaster();

            if (typeof images360.handleHovering === 'function') {
                images360.handleHovering = function () {
                    const mouse = this.viewer.inputHandler.mouse;
                    const camera = this.viewer.scene.getActiveCamera();
                    const domElement = this.viewer.renderer.domElement;

                    const ray = Potree.Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
                    raycaster.ray.copy(ray);
                    raycaster.camera = camera;

                    // Collect all visible sprites from ALL Images360 instances
                    const allVisibleSprites = [];
                    if (this.viewer && this.viewer.scene && this.viewer.scene.images360) {
                        this.viewer.scene.images360.forEach((images360Instance) => {
                            if (images360Instance.node && images360Instance.node.children) {
                                images360Instance.node.children.forEach((child) => {
                                    if (child.visible) {
                                        allVisibleSprites.push({
                                            object: child,
                                            parentImages360: images360Instance,
                                        });
                                    }
                                });
                            }
                        });
                    }

                    const intersections = raycaster.intersectObjects(
                        allVisibleSprites.map((item) => item.object),
                        false
                    );

                    let newHoveredObject = null;
                    let hoveredImages360Instance = null;

                    if (intersections.length > 0) {
                        // Get the closest intersection
                        const closestIntersection = intersections[0];
                        newHoveredObject = closestIntersection.object;

                        // Find which Images360 instance this object belongs to
                        const spriteInfo = allVisibleSprites.find((item) => item.object === newHoveredObject);
                        hoveredImages360Instance = spriteInfo ? spriteInfo.parentImages360 : null;
                    }

                    // Clear hover state from ALL Images360 instances first
                    if (this.viewer && this.viewer.scene && this.viewer.scene.images360) {
                        this.viewer.scene.images360.forEach((images360Instance) => {
                            if (images360Instance.currentlyHovered && images360Instance !== hoveredImages360Instance) {
                                // Clear hover from other instances
                                if (images360Instance.currentlyHovered) {
                                    if (!images360Instance.currentlyHovered.userData.originalScale) {
                                        images360Instance.currentlyHovered.userData.originalScale = new THREE.Vector3(
                                            1,
                                            1,
                                            1
                                        );
                                    }
                                    if (!images360Instance.currentlyHovered.userData.targetScale) {
                                        images360Instance.currentlyHovered.userData.targetScale =
                                            images360Instance.currentlyHovered.scale.clone();
                                    }
                                    images360Instance.currentlyHovered.userData.targetScale.copy(
                                        images360Instance.currentlyHovered.userData.originalScale
                                    );
                                    images360Instance.currentlyHovered.userData.isAnimating = true;
                                    images360Instance.currentlyHovered.userData.isCurrentlyHovered = false;
                                }
                                images360Instance.currentlyHovered = null;
                            }
                        });
                    }

                    // Only proceed with hover logic if this is the correct instance
                    if (hoveredImages360Instance !== this) {
                        return;
                    }

                    if (newHoveredObject !== this.currentlyHovered) {
                        if (this.currentlyHovered) {
                            if (!this.currentlyHovered.userData.originalScale) {
                                this.currentlyHovered.userData.originalScale = new THREE.Vector3(1, 1, 1);
                            }
                            if (!this.currentlyHovered.userData.targetScale) {
                                this.currentlyHovered.userData.targetScale = this.currentlyHovered.scale.clone();
                            }

                            this.currentlyHovered.userData.targetScale.copy(
                                this.currentlyHovered.userData.originalScale
                            );
                            this.currentlyHovered.userData.isAnimating = true;
                            this.currentlyHovered.userData.isCurrentlyHovered = false;
                        }

                        if (newHoveredObject) {
                            newHoveredObject.userData.isCurrentlyHovered = true;

                            const meshHoverOptions = newHoveredObject.userData.hoverOptions || hoverOptions;
                            const scaleMultiplier = meshHoverOptions.scaleMultiplier || 1.15;

                            if (newHoveredObject.userData.originalScale) {
                                const targetScale = newHoveredObject.userData.originalScale
                                    .clone()
                                    .multiplyScalar(scaleMultiplier);
                                newHoveredObject.userData.targetScale.copy(targetScale);
                                newHoveredObject.userData.isAnimating = true;
                            }
                        }

                        this.currentlyHovered = newHoveredObject;
                    }
                };
            }

            if (typeof images360.update === 'function') {
                images360.update = function () {
                    const previouslyHovered = this.currentlyHovered;

                    // Handle scale animations
                    this.images.forEach((image360) => {
                        if (image360.mesh && image360.mesh.userData.isAnimating) {
                            const mesh = image360.mesh;
                            const currentScale = mesh.scale;
                            const targetScale = mesh.userData.targetScale;
                            const animationSpeed = 0.1;

                            currentScale.lerp(targetScale, animationSpeed);

                            const distance = currentScale.distanceTo(targetScale);
                            if (distance < 0.001) {
                                currentScale.copy(targetScale);
                                mesh.userData.isAnimating = false;
                            }
                        }
                    });

                    // Clear all material effects first
                    this.images.forEach((image360) => {
                        if (image360.mesh && image360.mesh !== this.currentlyHovered) {
                            const shouldBeVisible = image360.visible;
                            if (image360.mesh.visible !== shouldBeVisible) {
                                image360.mesh.visible = shouldBeVisible;
                            }
                        }
                    });

                    if (this.selectingEnabled) {
                        this.handleHovering();
                    } else {
                        this.currentlyHovered = null;
                    }

                    if (previouslyHovered != this.currentlyHovered) {
                        if (previouslyHovered) {
                            if (!previouslyHovered.userData.originalScale) {
                                previouslyHovered.userData.originalScale = new THREE.Vector3(1, 1, 1);
                            }
                            if (!previouslyHovered.userData.targetScale) {
                                previouslyHovered.userData.targetScale = previouslyHovered.scale.clone();
                            }

                            previouslyHovered.userData.targetScale.copy(previouslyHovered.userData.originalScale);
                            previouslyHovered.userData.isAnimating = true;
                            previouslyHovered.userData.isCurrentlyHovered = false;
                        }

                        // Scale up newly hovered object
                        if (this.currentlyHovered) {
                            this.currentlyHovered.userData.isCurrentlyHovered = true;

                            const meshHoverOptions = this.currentlyHovered.userData.hoverOptions || hoverOptions;
                            const scaleMultiplier = meshHoverOptions.scaleMultiplier || 1.15;

                            if (!this.currentlyHovered.userData.originalScale) {
                                this.currentlyHovered.userData.originalScale = new THREE.Vector3(1, 1, 1);
                            }
                            if (!this.currentlyHovered.userData.targetScale) {
                                this.currentlyHovered.userData.targetScale = this.currentlyHovered.scale.clone();
                            }

                            const targetScale = this.currentlyHovered.userData.originalScale
                                .clone()
                                .multiplyScalar(scaleMultiplier);
                            this.currentlyHovered.userData.targetScale.copy(targetScale);
                            this.currentlyHovered.userData.isAnimating = true;
                        }
                    }
                };
            }
        },
    },

    Images360: {
        load: (image360) => {
            if (image360.texturePromise) {
                return image360.texturePromise;
            }
            return loadTextureOnDemand(image360);
        },

        setVisible: function (isVisible: boolean) {
            this.images.forEach((image360) => {
                if (image360.mesh) {
                    image360.mesh.visible = isVisible;
                }
            });
        },
    },
};
